import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { getSystemStatusHeightByRatio } from '@kakaopay-common/payweb-bridge';
import { Device } from '@kakaopay-common/user-agent';
import { Typography } from '@kakaopay-web-ui/components';

import { getItem, setItem } from 'src/helpers/sessionStorage';

import './style.scss';

interface ButtonProps {
  children: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  width?: number | string;
  style?: React.CSSProperties;
  onClick?: VoidFunction;
}

export const TitleBarButton = ({
  children,
  ariaLabel,
  disabled = false,
  className,
  width,
  style,
  onClick,
}: ButtonProps) => {
  return (
    <button
      type="button"
      className={classNames('TitleBarButton', className)}
      style={{ width, ...style }}
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={() => {
        onClick?.();
      }}
    >
      {children}
    </button>
  );
};

interface CenterProps {
  children: React.ReactNode;
  width?: number | string;
}

const isIOS = Device.isIos();
const isAOS = Device.isAndroid();

/**
 * TitleBar 컴포넌트 안쪽에 children으로 제공하는 컴포넌트
 * children으로 추가하는 속성은 타이틀바 중앙에 위치한다
 */
export const TitleBarCenter = ({ children, width }: CenterProps) => {
  return (
    <div className="TitleBarCenter" style={{ width }}>
      <div className="TitleBarCenter__inner">{children}</div>
    </div>
  );
};

interface RightProps {
  children: React.ReactNode;
  width?: number | string;
}

/**
 * TitleBar 컴포넌트 안쪽에 children으로 제공하는 컴포넌트
 * children으로 추가하는 속성은 타이틀바 우측에 위치한다
 */
export const TitleBarRight = ({ children, width }: RightProps) => {
  return (
    <div className="TitleBarRight" style={{ width }}>
      {children}
    </div>
  );
};

/**
 * TitleBar 컴포넌트 안쪽에 children으로 제공하는 컴포넌트
 * children으로 추가하는 속성은 타이틀바 좌측에 위치한다
 * 반드시 클라이언트 네비게이션 닫기 또는 뒤로가기 버튼을 비활성화 시키고 사용해야 함
 */
export const TitleBarLeft = ({ children, width }: RightProps) => {
  return (
    <div className="TitleBarLeft" style={{ width }}>
      {children}
    </div>
  );
};

export const TITLE_BAR_HEIGHT_SESSION_KEY = 'title-bar-height-session-key';

export const STATUS_BAR_HEIGHT = 21;
export const TITLE_BAR_HEIGHT = isIOS ? 44 : 52;
const TITLE_BAR_SIDE_BUTTON_WIDTH = 84;

interface TitleBarOffset {
  left: number;
  top: number;
  width: number;
  height: number;
}

let globalTitleBarOffset: TitleBarOffset;

interface Props {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  useBorder?: boolean;
  contentAlignLeft?: boolean;
  staticMode?: boolean;
  scrollingShow?: {
    showTop: number;
  };
  onLoad?: (offset: TitleBarOffset) => void;
}

/**
 * 타이틀바 영역
 * 자식 ReactNode를 우선으로 표현하며 그 다음으로 title 값을 보고 title을 설정한다
 */
const TitleBar = ({
  className = '',
  children,
  title = '',
  useBorder = false,
  contentAlignLeft = true,
  scrollingShow,
  staticMode = false,
  onLoad,
}: Props) => {
  const [isShowTitleContent, setIsShowTitleContent] = useState(!scrollingShow);
  const titleBarRef = useRef<HTMLDivElement>(null);

  /** android 타이틀바 높이 (ios는 스타일에서 처리) */
  const [statusBarHeight, setStatusBarHeight] = useState(STATUS_BAR_HEIGHT);

  const renderTitleContents = useCallback(() => {
    return (
      children ||
      (title && (
        <TitleBarCenter>
          <Typography
            type="subtitle18"
            color="high-emphasis"
            weight="bold"
            tag="h1"
          >
            {title}
          </Typography>
        </TitleBarCenter>
      ))
    );
  }, [children, title]);

  useLayoutEffect(() => {
    if (!scrollingShow) {
      return;
    }

    // 새로고침 시 바로 대응
    setIsShowTitleContent(window.scrollY > scrollingShow.showTop);

    const handleScroll = () => {
      setIsShowTitleContent(window.scrollY > scrollingShow.showTop);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollingShow]);

  useLayoutEffect(() => {
    (async () => {
      try {
        if (!getItem(TITLE_BAR_HEIGHT_SESSION_KEY)) {
          // android만 제공함, ios는 스타일에서 처리
          const systemStatusHeightByRatio =
            (await getSystemStatusHeightByRatio()).result.height ||
            STATUS_BAR_HEIGHT;

          // NOTE: 안드로이드에서 시스템 영역의 높이를 소수점 4자리 toFixed(긴 소수점 절삭)
          const barHeight = Number(systemStatusHeightByRatio.toFixed(4));

          setItem(TITLE_BAR_HEIGHT_SESSION_KEY, barHeight);
          setStatusBarHeight(barHeight);
        }
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    if (!titleBarRef.current) {
      return;
    }

    const titleBarElement = titleBarRef.current;

    // globalTitleBarOffset에도 cache했다가 useTitleBarOffset에서 사용
    const titleBarOffset = (globalTitleBarOffset = {
      left: titleBarElement.offsetLeft,
      top: titleBarElement.offsetTop,
      width: titleBarElement.offsetWidth,
      height: titleBarElement.offsetHeight,
    });

    // TitleBar Element Load 시 onLoad 이벤트를 통해 offset 정보 제공
    onLoad?.(titleBarOffset);
  }, [onLoad, titleBarRef]);

  return (
    <div
      ref={titleBarRef}
      className={classNames(
        'TitleBar',
        {
          TitleBar__border: useBorder || (scrollingShow && isShowTitleContent),
          'TitleBar__is-show': isShowTitleContent,
          'TitleBar__is-aos': isAOS,
          'TitleBar__is-ios': isIOS,
          'TitleBar__align-left': contentAlignLeft,
          'TitleBar__static-mode': staticMode,
        },
        className
      )}
      style={
        {
          '--status-bar-height': `${
            getItem(TITLE_BAR_HEIGHT_SESSION_KEY) ?? statusBarHeight
          }px`,
          '--title-bar-height': `${TITLE_BAR_HEIGHT}px`,
          '--title-bar-side-button-width': `${TITLE_BAR_SIDE_BUTTON_WIDTH}px`,
        } as React.CSSProperties
      }
    >
      <div className="TitleBar__content">{renderTitleContents()}</div>
    </div>
  );
};

export default TitleBar;

/**
 * 타이틀 바의 offset 정보를 제공하는 hook
 */
export const useTitleBarOffset = () => {
  const getTitleBarOffset = useCallback(() => {
    return globalTitleBarOffset;
    // global 영역에 있는 변수의 변화도 감지함
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTitleBarOffset]);
  return { getTitleBarOffset };
};
